import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AgentManagement.css';
import api from '../../api/axios.js';
import { FiEye, FiEdit, FiTrash2, FiSearch, FiChevronDown, FiUsers } from "react-icons/fi";
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

const AgentManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({});
  const [totalAgents, setTotalAgents] = useState(0);

  // Filter states
  const [selectedState, setSelectedState] = useState('');
  const [selectedRole, setSelectedRole] = useState('advisor-district-coordinator');
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');

  // States list (you can expand this based on your needs)
  const states = [
    'up', 'maharashtra', 'gujarat', 'rajasthan', 'punjab', 'haryana',
    'delhi', 'west-bengal', 'tamil-nadu', 'karnataka', 'kerala', 'andhra-pradesh'
  ];

  const STATES = [

    { label: "Andhra Pradesh", value: "andhra pradesh" },
    { label: "Arunachal Pradesh", value: "arunachal pradesh" },
    { label: "Assam", value: "assam" },
    { label: "Bihar", value: "bihar" },
    { label: "Chhattisgarh", value: "chhattisgarh" },
    { label: "Goa", value: "goa" },
    { label: "Gujarat", value: "gujarat" },
    { label: "Haryana", value: "haryana" },
    { label: "Himachal Pradesh", value: "himachal pradesh" },
    { label: "Jharkhand", value: "jharkhand" },
    { label: "Karnataka", value: "karnataka" },
    { label: "Kerala", value: "kerala" },
    { label: "Madhya Pradesh", value: "madhya pradesh" },
    { label: "Maharashtra", value: "maharashtra" },
    { label: "Manipur", value: "manipur" },
    { label: "Meghalaya", value: "meghalaya" },
    { label: "Mizoram", value: "mizoram" },
    { label: "Nagaland", value: "nagaland" },
    { label: "Odisha", value: "odisha" },
    { label: "Punjab", value: "punjab" },
    { label: "Rajasthan", value: "rajasthan" },
    { label: "Sikkim", value: "sikkim" },
    { label: "Tamil Nadu", value: "tamil nadu" },
    { label: "Telangana", value: "telangana" },
    { label: "Tripura", value: "tripura" },
    { label: "Uttar Pradesh", value: "uttar pradesh" },
    { label: "Uttarakhand", value: "uttarakhand" },
    { label: "West Bengal", value: "west bengal" },
    { label: "Delhi", value: "delhi" },
    { label: "Jammu & Kashmir", value: "jammu and kashmir" },
    { label: "Ladakh", value: "ladakh" },
    { label: "Chandigarh", value: "chandigarh" },
    { label: "Puducherry", value: "puducherry" },
    { label: "Andaman & Nicobar", value: "andaman and nicobar" },
    { label: "Dadra & Nagar Haveli", value: "dadra and nagar haveli" },
    { label: "Daman & Diu", value: "daman and diu" },
    { label: "Lakshadweep", value: "lakshadweep" }
  ];

  const roles = [
    { value: 'advisor-district-coordinator', label: 'District Coordinator' },
    { value: 'advisor-commisionerate', label: 'Commissionery Manager' },
    { value: 'advisor-state-head', label: 'State Head' }
  ];

  const fetchAgents = async (page = 1) => {
    try {
      setLoading(true);

      const params = {
        roleType: 'customer',
        userType: selectedRole,
        page,
        limit: 10,
        ...(selectedState && { state: selectedState }),
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
      };

      const res = await api.get("/admin/all-users", { params });

      setAgents(res.data.data || []);
      setPagination(res.data.pagination || {});
      setTotalAgents(res.data.totalAdvisors || 0);

    } catch (error) {
      console.error("Fetch agents error:", error);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents(1);
  }, [selectedState, selectedRole, debouncedSearch]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown')) {
        setStateDropdownOpen(false);
        setRoleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNextPage = () => {
    if (pagination.page < pagination.totalPages) {
      fetchAgents(pagination.page + 1);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchAgents(pagination.page - 1);
    }
  };

  const formatRoleName = (userType) => {
    const roleMap = {
      'advisor-district-coordinator': 'District Coordinator',
      'advisor-commisionerate': 'Commissionery Manager',
      'advisor-state-head': 'State Head'
    };
    return roleMap[userType] || userType;
  };

  const formatTarget = (target) => {
    if (!target || !target.assigned) return '0';
    return `${target.assigned.toLocaleString()}`;
  };

  return (
    <div className="agent-management">
      <div className="agent-management-header">
        <h1>Agent Management</h1>
      </div>

      {/* Stats Card */}
      

       <div className="stats-cards">
        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">Total Advisor</span>
            <span className="stat-value">{totalAgents}</span>
          </div>
          <div className="stat-icon leads"><FiUsers /></div>
        </div>

        <div className="stat-card">
          <div className="stat-info">
            <span className="stat-label">{selectedRole==="advisor-district-coordinator"?"District Coordinator":selectedRole==="advisor-commisionerate"?"Commisionery Manager":"State Head"}</span>
            <span className="stat-value">{pagination.totalRecords}</span>
          </div>
          <div className="stat-icon leads"><FiUsers /></div>
        </div>
      

      </div>

     

      {/* Main Table Section */}
      <div className="table-section">
        <div className="table-header">
          <h3>👥 Advisor Table</h3>

          <div className="table-actions">
            {/* Search Box */}
            <div className="search-box">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="search-icon"><FiSearch /></span>
            </div>

            {/* State Dropdown */}
            <div className="custom-dropdown">
              <button
                className="dropdown-trigger"
                onClick={() => setStateDropdownOpen(!stateDropdownOpen)}
              >
                {selectedState || 'State'} <FiChevronDown />
              </button>
              {stateDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-search">
                    <input
                      type="text"
                      placeholder="Search states..."
                      value={stateSearchTerm}
                      onChange={(e) => setStateSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="dropdown-option" onClick={() => {
                    setSelectedState('');
                    setStateDropdownOpen(false);
                  }}>
                    All States
                  </div>

                  {STATES
                    .filter(state =>
                      state.value.toLowerCase().includes(stateSearchTerm.toLowerCase())
                    )
                    .map(state => (
                      <div
                        key={state.value}
                        className="dropdown-option"
                        onClick={() => {
                          setSelectedState(state.value);
                          setStateDropdownOpen(false);
                        }}
                      >
                        {state.label}
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Role Dropdown */}
            <div className="custom-dropdown">
              <button
                className="dropdown-trigger"
                onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
              >
                {selectedRole ? roles.find(r => r.value === selectedRole)?.label : 'Role'} <FiChevronDown />
              </button>
              {roleDropdownOpen && (
                <div className="dropdown-menu">
                  {/* <div className="dropdown-option" onClick={() => {
                    setSelectedRole('');
                    setRoleDropdownOpen(false);
                  }}>
                    All Roles
                  </div> */}
                  {roles.map(role => (
                    <div
                      key={role.value}
                      className="dropdown-option"
                      onClick={() => {
                        setSelectedRole(role.value);
                        setRoleDropdownOpen(false);
                      }}
                    >
                      {role.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="data-table">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading agents...</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Agent Name</th>
                  <th>Agent Code</th>
                  <th>Role</th>
                  <th>Target (₹)</th>
                  <th>Phone Number</th>
                  <th>Email</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {agents.length > 0 ? (
                  agents.map((agent, index) => (
                    <tr key={agent._id} className="table-row-animated" style={{ animationDelay: `${index * 0.1}s` }}>
                     <td>
  {agent?.name
    ? agent.name.charAt(0).toUpperCase() + agent.name.slice(1)
    : 'N/A'}
</td>

<td>
  {agent?._id?.slice(-6) || 'N/A'}
</td>

<td>
  <span className="role-badge">
    {agent?.userType
      ? formatRoleName(agent.userType)
      : 'N/A'}
  </span>
</td>

<td>
  {formatTarget(agent?.target) ?? 'N/A'}
</td>

<td>
  {agent?.mobile_number
    ? String(agent.mobile_number).trim()
    : 'N/A'}
</td>

<td>
  {agent?.email?.trim() || 'N/A'}
</td>
                      <td>
                        <div className="action-icons">
                          <button
                            className="icon-btn view"
                            onClick={() => navigate(`/agent-management/${agent._id}`)}
                            title="View Details"
                          >
                            <FiEye />
                          </button>
                          {/* <button
                            className="icon-btn edit"
                            title="Edit Agent"
                          >
                            <FiEdit />
                          </button>
                          <button 
                            className="icon-btn delete"
                            title="Delete Agent"
                          >
                            <FiTrash2 />
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="no-data">
                      {loading ? 'Loading...' : 'No agents found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={handlePrevPage}
                className={pagination.page === 1 ? "pagination-disabled" : "pagination-btn"}
                disabled={pagination.page === 1}
              >
                Prev
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={pagination.page === pagination.totalPages}
                className={
                  pagination.page === pagination.totalPages
                    ? "pagination-disabled"
                    : "pagination-btn"
                }
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentManagement;